/**
 * Merge-jar U chrome (open-top glass). Shared path for Canvas 2D fallback and
 * Pixi Graphics. Fill sits behind bodies; rims sit in front.
 */

export function clampJarURadius(r, w, h) {
  return Math.max(0, Math.min(r || 0, (w || 0) / 2, h || 0));
}

/**
 * Trace an open-top U (straight sides, rounded bottom). `g` is a canvas ctx or
 * Pixi Graphics (moveTo / lineTo / quadraticCurveTo).
 * @param {{ moveTo: Function, lineTo: Function, quadraticCurveTo: Function, closePath?: Function }} g
 * @param {boolean} [closeTop] close across the mouth so the shape can fill
 */
export function traceJarUPath(g, x, top, w, h, r, closeTop) {
  var width = Math.max(1, w || 1);
  var height = Math.max(1, h || 1);
  var corner = clampJarURadius(r, width, height);
  var bottom = top + height;
  g.moveTo(x, top);
  g.lineTo(x, bottom - corner);
  g.quadraticCurveTo(x, bottom, x + corner, bottom);
  g.lineTo(x + width - corner, bottom);
  g.quadraticCurveTo(x + width, bottom, x + width, bottom - corner);
  g.lineTo(x + width, top);
  if (closeTop) {
    if (typeof g.closePath === "function") g.closePath();
    else g.lineTo(x, top);
  }
}

/**
 * Board-space boxes for the glass fill + inner/outer rims.
 * @param {{ neckLeft: number, neckRight: number, T: number, bodyBottom: number }} geom
 * @param {{ x?: number, y?: number }} origin flask panel origin
 * @param {number} cornerRadius
 */
export function layoutShakeJarChrome(geom, origin, cornerRadius) {
  if (!geom) return null;
  var ox = origin && origin.x != null ? origin.x : 0;
  var oy = origin && origin.y != null ? origin.y : 0;
  var innerW = Math.max(24, geom.neckRight - geom.neckLeft);
  var innerH = Math.max(24, geom.bodyBottom - geom.T);
  var jarR = clampJarURadius(cornerRadius, innerW, innerH);
  var x = ox + geom.neckLeft;
  var y = oy + geom.T;
  return {
    x: x,
    y: y,
    width: innerW,
    height: innerH,
    radius: jarR,
    innerX: x + 3,
    innerY: y,
    innerWidth: Math.max(1, innerW - 6),
    innerHeight: Math.max(1, innerH - 3),
    innerRadius: Math.max(2, jarR - 2),
  };
}

/**
 * Publish glass fill (under sprites) + rims (over sprites). No-op without a playfield.
 * @returns {boolean} true when GPU chrome was published
 */
export function syncShakeJarChrome(playfield, layout) {
  if (!playfield || !layout) return false;
  if (typeof playfield.underFx !== "function" || typeof playfield.effect !== "function") {
    return false;
  }
  var box = {
    kind: "jar-u",
    x: layout.x,
    y: layout.y,
    width: layout.width,
    height: layout.height,
    radius: layout.radius,
  };
  playfield.underFx(Object.assign({}, box, {
    key: "jar-fill",
    closeTop: true,
    fill: 0x5aa5d7,
    fillAlpha: 0.28,
    zIndex: 0,
  }));
  playfield.effect(Object.assign({}, box, {
    key: "jar-rim-glow",
    stroke: 12,
    color: "#78c8f5",
    strokeAlpha: 0.22,
    zIndex: 18,
  }));
  playfield.effect(Object.assign({}, box, {
    key: "jar-rim",
    stroke: 3,
    color: "#aad7f5",
    strokeAlpha: 0.85,
    zIndex: 19,
  }));
  playfield.effect({
    key: "jar-rim-inner",
    kind: "jar-u",
    x: layout.innerX,
    y: layout.innerY,
    width: layout.innerWidth,
    height: layout.innerHeight,
    radius: layout.innerRadius,
    stroke: 1,
    color: "#dcf0ff",
    strokeAlpha: 0.45,
    zIndex: 20,
  });
  return true;
}
